import { useEffect, useSyncExternalStore } from "react";
import { platformService, type LoadTestPoolsResponse } from "../../../api/platform.service.js";

export type LoadTestAccountsAvailability = "unknown" | "active" | "inactive";

let availability: LoadTestAccountsAvailability = "unknown";
let generation = 0;
let inflight: Promise<LoadTestPoolsResponse> | null = null;
const listeners = new Set<() => void>();

export function isNotFoundError(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 404;
}

function setAvailability(next: LoadTestAccountsAvailability): void {
  if (availability === next) return;
  availability = next;
  for (const listener of listeners) listener();
}

export function resetLoadTestAccountsAvailability(): void {
  generation += 1;
  inflight = null;
  setAvailability("unknown");
}

export function fetchLoadTestPools(): Promise<LoadTestPoolsResponse> {
  if (inflight) return inflight;
  const requestGeneration = generation;
  const current = () => requestGeneration === generation;
  const request = platformService.loadTestAccounts.list().then(
    response => {
      if (current()) setAvailability("active");
      return response;
    },
    (error: unknown) => {
      if (current()) {
        if (isNotFoundError(error)) setAvailability("inactive");
        else if (availability === "unknown") setAvailability("active");
      }
      throw error;
    },
  );
  inflight = request;
  const clear = () => {
    if (inflight === request) inflight = null;
  };
  request.then(clear, clear);
  return request;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getAvailability(): LoadTestAccountsAvailability {
  return availability;
}

export function useLoadTestAccountsAvailability(enabled: boolean): LoadTestAccountsAvailability {
  const current = useSyncExternalStore(subscribe, getAvailability, getAvailability);
  useEffect(() => {
    if (enabled && availability === "unknown") fetchLoadTestPools().catch(() => undefined);
  }, [enabled]);
  return current;
}
